import { useMemo } from 'react'

import { useCurrentOrgId, useIsCloaking } from '~/store/slices/currentOrg'

import type { SolarArrayModel } from '../../client/ders'
import useManagedSites from './sites'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type SolarArrayFilters = {
  orgId?: string
  siteId?: string
  nodeId?: string
}

type UseManagedSolarArraysInput = {
  filters?: SolarArrayFilters
}

export default function useManagedSolarArrays({ filters }: UseManagedSolarArraysInput = {}) {
  const { solarArrays } = useDataManager()
  const { data, isLoading, isFetching } = solarArrays

  // The manager fetches with `orgIds: [principalOrgId]` + a deep orgDepth,
  // so the cached set is already scoped to the principal user's org tree.
  // The site-join only does real narrowing when the caller wants a sub-slice
  // — i.e. while cloaking, or when an explicit `orgId` filter is passed.
  // Skipping the join (and its loading wait) on the common no-narrowing path
  // keeps the page snappy without sacrificing correctness when narrowing is
  // actually needed.
  const isCloaking = useIsCloaking()
  const needsSiteJoin = isCloaking || filters?.orgId != null

  const { sites: orgSites, isLoading: isSitesLoading, isFetching: isSitesFetching } = useManagedSites({
    filters: { orgId: filters?.orgId }
  })

  return {
    solarArrays: useFilteredSolarArrays(data, orgSites, needsSiteJoin, filters),
    isLoading: isLoading || (needsSiteJoin && isSitesLoading),
    isFetching: isFetching || (needsSiteJoin && isSitesFetching)
  }
}

function useFilteredSolarArrays(
  data: SolarArrayModel[],
  orgSites: ReturnType<typeof useManagedSites>['sites'],
  needsSiteJoin: boolean,
  filters?: SolarArrayFilters
) {
  const currentOrgId = useCurrentOrgId()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  const allowedSiteIds = useMemo(() => new Set(orgSites.map((site) => site.depotId)), [orgSites])

  return useMemo(() => {
    const { siteId, nodeId } = normalizedFilters

    if (siteId === EMPTY || nodeId === EMPTY) {
      return []
    }

    let filtered = needsSiteJoin
      ? data.filter((solarArray) => allowedSiteIds.has(solarArray.siteId))
      : data

    if (siteId != null) {
      filtered = filtered.filter((solarArray) => solarArray.siteId === siteId)
    }

    if (nodeId != null) {
      filtered = filtered.filter((solarArray) => solarArray.nodeId === nodeId)
    }

    return filtered
  }, [data, allowedSiteIds, needsSiteJoin, normalizedFilters])
}
